import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { projectsApi } from "../../api/projects";
import { useProjectSettingsDirty } from "./ProjectSettingsUnsavedContext";
import { erpApi } from "../../api/erp";
import ContextualHelp from "./ContextualHelp";
import DashboardLoadingState from "./DashboardLoadingState";
import {
  FINANCIAL_VISIBILITY_LABELS,
  PERMISSION_GROUPS,
  PROJECT_STATUS_LABELS,
  effectivePermissionsForMember,
  projectRoleLabel,
} from "../../utils/projectAccess";

const BRAND = { orange: "#F35A3A", dark: "#19242F", border: "rgba(25,36,47,0.09)" };

function entityId(entity = {}) {
  return String(entity?.id || entity?._id || entity || "");
}

function memberUser(member = {}) {
  return member.userId || member.user || {};
}

function emptyMemberForm() {
  return {
    email: "",
    role: "viewer",
    status: "active",
    jobTitle: "",
    notes: "",
    permissions: [],
    deniedPermissions: [],
    scopes: {
      cashRegisterIds: [],
      branchCodes: [],
      ownOperationsOnly: false,
      financialVisibility: "operational",
    },
  };
}


function AccessScopeSection({ mobile, editing, registers, updateScope }) {
  const content = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField select SelectProps={{ multiple: true }} fullWidth label="Cajas y cuentas financieras asignadas" value={editing.scopes.cashRegisterIds} onChange={(event) => updateScope("cashRegisterIds", typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value)} helperText={registers.length ? "Para cajeros, seleccioná únicamente la caja que operan. Así no acceden a bancos, billeteras u otras cajas." : "No fue posible consultar las cajas o todavía no existen."}>
            {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)}>{register.name || register.code || "Caja"}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Sucursales asignadas" value={(editing.scopes.branchCodes || []).join(", ")} onChange={(event) => updateScope("branchCodes", event.target.value.split(",").map((value) => value.trim()).filter(Boolean))} helperText="Códigos separados por coma. Vacío permite todas." />
        </Grid>
      </Grid>
      <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={editing.scopes.ownOperationsOnly === true} onChange={(event) => updateScope("ownOperationsOnly", event.target.checked)} />} label="Limitar consultas a operaciones propias" />
    </>
  );

  if (!mobile) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 900, color: BRAND.dark, mb: 1.5 }}>Alcance operativo</Typography>
        {content}
      </Paper>
    );
  }

  return (
    <Accordion disableGutters elevation={0} sx={{ border: `1px solid ${BRAND.border}`, borderRadius: "14px !important", overflow: "hidden", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 52, px: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Alcance operativo</Typography>
          <Typography variant="caption" color="text.secondary">Qué cajas puede operar y qué información puede consultar</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.6 }}>{content}</AccordionDetails>
    </Accordion>
  );
}

function PermissionGroupSection({ group, mobile, effective, rolePreset, togglePermission, defaultExpanded = false }) {
  const permissions = (
    <Stack>
      {group.permissions.map(([permission, label]) => (
        <FormControlLabel
          key={permission}
          control={<Checkbox checked={effective.has(permission)} onChange={(event) => togglePermission(permission, event.target.checked)} />}
          label={<Box><Typography variant="body2" sx={{ fontWeight: 750 }}>{label}</Typography>{rolePreset.has(permission) ? <Typography variant="caption" color="text.secondary">Incluido por el rol</Typography> : null}</Box>}
          sx={{ alignItems: "flex-start", py: 0.25, mx: 0 }}
        />
      ))}
    </Stack>
  );

  if (!mobile) {
    return (
      <Paper variant="outlined" sx={{ p: 1.8, borderRadius: 3, height: "100%" }}>
        <Typography sx={{ fontWeight: 900, color: BRAND.dark, mb: 1 }}>{group.label}</Typography>
        {permissions}
      </Paper>
    );
  }

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ border: `1px solid ${BRAND.border}`, borderRadius: "14px !important", overflow: "hidden", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 52, px: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: "100%", minWidth: 0, pr: .5 }}>
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{group.label}</Typography>
          <Chip size="small" label={`${group.permissions.filter(([permission]) => effective.has(permission)).length}/${group.permissions.length}`} variant="outlined" sx={{ height: 24, fontWeight: 850 }} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.2 }}>{permissions}</AccordionDetails>
    </Accordion>
  );
}

function normalizeMember(member = {}) {
  return {
    ...emptyMemberForm(),
    ...member,
    id: entityId(member),
    permissions: Array.isArray(member.permissions) ? member.permissions : [],
    deniedPermissions: Array.isArray(member.deniedPermissions) ? member.deniedPermissions : [],
    scopes: {
      ...emptyMemberForm().scopes,
      ...(member.scopes || {}),
      cashRegisterIds: Array.isArray(member.scopes?.cashRegisterIds)
        ? member.scopes.cashRegisterIds.map(String)
        : [],
      branchCodes: Array.isArray(member.scopes?.branchCodes)
        ? member.scopes.branchCodes.map(String)
        : [],
    },
  };
}

function editableMemberSnapshot(member = {}) {
  const normalized = normalizeMember(member);
  return {
    role: normalized.role,
    status: normalized.status,
    jobTitle: normalized.jobTitle || "",
    notes: normalized.notes || "",
    permissions: [...(normalized.permissions || [])].map(String).sort(),
    deniedPermissions: [...(normalized.deniedPermissions || [])].map(String).sort(),
    scopes: {
      cashRegisterIds: [...(normalized.scopes?.cashRegisterIds || [])].map(String).sort(),
      branchCodes: [...(normalized.scopes?.branchCodes || [])].map(String).sort(),
      ownOperationsOnly: normalized.scopes?.ownOperationsOnly === true,
      financialVisibility: normalized.scopes?.financialVisibility || "operational",
    },
  };
}

export default function ProjectAccessManager({ projectId, currentMember = null }) {
  const phone = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [members, setMembers] = React.useState([]);
  const [catalog, setCatalog] = React.useState({ roles: [], statuses: [], permissions: [], rolePresets: {}, capabilities: {}, financialVisibilityLevels: [] });
  const [registers, setRegisters] = React.useState([]);
  const [newMember, setNewMember] = React.useState(emptyMemberForm);
  const [editing, setEditing] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [memberActions, setMemberActions] = React.useState({ anchor: null, member: null });

  const accessAreaDirty = React.useMemo(() => {
    const empty = emptyMemberForm();
    const newMemberDirty = JSON.stringify(newMember) !== JSON.stringify(empty);
    if (!editing) return newMemberDirty;
    const original = members.find((member) => entityId(member) === String(editing.id || ""));
    const editingDirty = original
      ? JSON.stringify(editableMemberSnapshot(editing)) !== JSON.stringify(editableMemberSnapshot(original))
      : false;
    return newMemberDirty || editingDirty;
  }, [newMember, editing, members]);
  useProjectSettingsDirty("access", accessAreaDirty);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await projectsApi.listMembers(projectId);
      setMembers(data.members || []);
      setCatalog(data.accessCatalog || {});
      try {
        const cashData = await erpApi.listCashRegisters(projectId);
        setRegisters((cashData.registers || []).map((item) => item.register || item));
      } catch (_error) {
        setRegisters([]);
      }
    } catch (err) {
      setError(err.message || "No se pudieron cargar los accesos");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const addMember = async (event) => {
    event.preventDefault();
    if (!newMember.email.trim()) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.addMember(projectId, {
        ...newMember,
        email: newMember.email.trim().toLowerCase(),
      });
      setNewMember(emptyMemberForm());
      setMessage("Miembro agregado con su rol y alcance inicial.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo agregar el miembro");
    } finally {
      setSaving(false);
    }
  };

  const saveMember = async () => {
    if (!editing?.id) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.updateMember(projectId, editing.id, {
        role: editing.role,
        status: editing.status,
        jobTitle: editing.jobTitle,
        notes: editing.notes,
        permissions: editing.permissions,
        deniedPermissions: editing.deniedPermissions,
        scopes: editing.scopes,
      });
      setEditing(null);
      setMessage("Permisos y alcance actualizados.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el miembro");
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (member) => {
    const user = memberUser(member);
    if (!window.confirm(`¿Quitar el acceso de ${user.email || user.name || "este miembro"}?`)) return;
    try {
      setSaving(true);
      setError("");
      await projectsApi.removeMember(projectId, entityId(member));
      setMessage("Acceso eliminado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo eliminar el acceso");
    } finally {
      setSaving(false);
    }
  };

  const rolePreset = React.useMemo(
    () => new Set(catalog.rolePresets?.[editing?.role] || []),
    [catalog.rolePresets, editing?.role],
  );

  const effective = React.useMemo(
    () => new Set(editing ? effectivePermissionsForMember(editing, catalog) : []),
    [catalog, editing],
  );

  const visiblePermissionGroups = React.useMemo(() => {
    const available = new Set(catalog.permissions || []);
    return PERMISSION_GROUPS
      .map((group) => ({
        ...group,
        permissions: (group.permissions || []).filter(([permission]) => available.has(permission)),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [catalog.permissions]);

  const togglePermission = (permission, checked) => {
    setEditing((current) => {
      if (!current) return current;
      const granted = new Set(current.permissions || []);
      const denied = new Set(current.deniedPermissions || []);
      const includedByRole = (catalog.rolePresets?.[current.role] || []).includes(permission);
      if (checked) {
        denied.delete(permission);
        if (!includedByRole) granted.add(permission);
      } else {
        granted.delete(permission);
        if (includedByRole) denied.add(permission);
      }
      return { ...current, permissions: [...granted], deniedPermissions: [...denied] };
    });
  };

  const changeRole = (role) => {
    setEditing((current) => current ? { ...current, role, permissions: [], deniedPermissions: [] } : current);
  };

  const updateScope = (field, value) => {
    setEditing((current) => current ? { ...current, scopes: { ...current.scopes, [field]: value } } : current);
  };

  const currentRole = String(currentMember?.role || "viewer");

  return (
    <Stack spacing={2.2}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>Equipo y accesos</Typography>
              <ContextualHelp
                compact
                title="Cómo funcionan los accesos"
                text="Cada persona parte de un rol y después puede tener permisos o alcances específicos. Los cambios afectan lo que puede ver y hacer dentro de este proyecto."
                bullets={["El owner conserva control total.", "Podés limitar cajas o sucursales sin crear otro rol.", "Suspender un acceso no elimina a la persona ni su historial."]}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
              Separá quién vende, cobra, factura, administra stock y ve los totales del negocio.
            </Typography>
          </Box>
          <Button startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading || saving} sx={{ textTransform: "none", fontWeight: 850 }}>
            Actualizar
          </Button>
        </Stack>
      </Paper>

      <Paper component="form" onSubmit={addMember} variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
        <Stack spacing={1.6}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonAddAltRoundedIcon sx={{ color: BRAND.orange }} />
            <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Agregar miembro existente</Typography>
          </Stack>
          <Alert severity="info" sx={{ display: { xs: "none", sm: "flex" } }}>La cuenta debe estar registrada y activa. Después podés personalizar sus permisos y cajas asignadas.</Alert>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth required type="email" label="Email de la cuenta" value={newMember.email} onChange={(event) => setNewMember((current) => ({ ...current, email: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField select fullWidth label="Rol inicial" value={newMember.role} onChange={(event) => setNewMember((current) => ({ ...current, role: event.target.value }))}>
                {(catalog.roles || []).filter((role) => currentRole === "owner" || role !== "owner").map((role) => <MenuItem key={role} value={role}>{projectRoleLabel(role)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Puesto o función" value={newMember.jobTitle} onChange={(event) => setNewMember((current) => ({ ...current, jobTitle: event.target.value }))} placeholder="Ej.: Caja turno tarde" />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Button type="submit" fullWidth variant="contained" startIcon={<PersonAddAltRoundedIcon />} disabled={saving || loading} sx={{ height: "100%", minHeight: 56, textTransform: "none", fontWeight: 900 }}>
                Agregar
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <DashboardLoadingState label="Cargando equipo…" minHeight={260} />
        ) : !members.length ? (
          <Box sx={{ p: 4, textAlign: "center" }}><Typography color="text.secondary">Todavía no hay miembros configurados.</Typography></Box>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {members.map((member) => {
              const user = memberUser(member);
              const isOwner = member.role === "owner";
              const permissionsCount = effectivePermissionsForMember(member, catalog).length;
              return (
                <Stack key={entityId(member)} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} sx={{ p: 2.2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{user.name || user.email || "Miembro"}</Typography>
                      <Chip size="small" label={projectRoleLabel(member.role)} />
                      <Chip size="small" color={member.status === "suspended" ? "warning" : "success"} label={PROJECT_STATUS_LABELS[member.status || "active"] || member.status} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>{user.email || "—"}{member.jobTitle ? ` · ${member.jobTitle}` : ""}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {permissionsCount} permisos efectivos · {FINANCIAL_VISIBILITY_LABELS[member.scopes?.financialVisibility || "operational"]}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => setEditing(normalizeMember(member))}
                      sx={{ display: { xs: "inline-flex", sm: "none" }, flex: 1, textTransform: "none", fontWeight: 900 }}
                    >
                      Editar acceso
                    </Button>
                    <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
                      <Tooltip title="Editar rol, permisos y alcance"><IconButton onClick={() => setEditing(normalizeMember(member))}><EditRoundedIcon /></IconButton></Tooltip>
                    </Box>
                    <Tooltip title="Más opciones">
                      <IconButton
                        aria-label={`Más opciones para ${user.name || user.email || "miembro"}`}
                        onClick={(event) => setMemberActions({ anchor: event.currentTarget, member })}
                        disabled={saving}
                      >
                        <MoreVertRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Menu
        anchorEl={memberActions.anchor}
        open={Boolean(memberActions.anchor)}
        onClose={() => setMemberActions({ anchor: null, member: null })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disabled={memberActions.member?.role === "owner" || saving}
          onClick={() => {
            const target = memberActions.member;
            setMemberActions({ anchor: null, member: null });
            if (target) removeMember(target);
          }}
          sx={{ color: "error.main", minHeight: 44 }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          Quitar acceso
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(editing)} onClose={saving ? undefined : () => setEditing(null)} fullScreen={phone} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 950 }}>Configurar acceso</DialogTitle>
        <Divider />
        {editing ? (
          <DialogContent sx={{ bgcolor: "#FAFBFD", px: { xs: 1.25, sm: 2.5 }, py: { xs: 1.25, sm: 2.5 } }}>
            <Stack spacing={2.2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label="Rol base" value={editing.role} disabled={editing.role === "owner" && currentRole !== "owner"} onChange={(event) => changeRole(event.target.value)}>
                    {(catalog.roles || []).filter((role) => currentRole === "owner" || role !== "owner").map((role) => <MenuItem key={role} value={role}>{projectRoleLabel(role)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label="Estado" value={editing.status} disabled={editing.role === "owner"} onChange={(event) => setEditing((current) => ({ ...current, status: event.target.value }))}>
                    {(catalog.statuses || ["active", "suspended"]).map((status) => <MenuItem key={status} value={status}>{PROJECT_STATUS_LABELS[status] || status}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Puesto" value={editing.jobTitle || ""} onChange={(event) => setEditing((current) => ({ ...current, jobTitle: event.target.value }))} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label="Visibilidad financiera" value={editing.scopes.financialVisibility} onChange={(event) => updateScope("financialVisibility", event.target.value)}>
                    {(catalog.financialVisibilityLevels || ["none", "operational", "full"]).map((level) => <MenuItem key={level} value={level}>{FINANCIAL_VISIBILITY_LABELS[level] || level}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              <AccessScopeSection mobile={phone} editing={editing} registers={registers} updateScope={updateScope} />

              <Box>
                <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Permisos efectivos</Typography>
                  <ContextualHelp compact title="Rol y permisos" text="El rol aporta una base. Un permiso activado manualmente se suma; uno desactivado puede bloquear una capacidad incluida por el rol." />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>El rol aporta una base. Cada interruptor puede conceder o bloquear una capacidad concreta.</Typography>
              </Box>
              <Grid container spacing={phone ? 1 : 1.5}>
                {visiblePermissionGroups.map((group, index) => (
                  <Grid item xs={12} md={6} key={group.key}>
                    <PermissionGroupSection
                      group={group}
                      mobile={phone}
                      effective={effective}
                      rolePreset={rolePreset}
                      togglePermission={togglePermission}
                      defaultExpanded={index === 0}
                    />
                  </Grid>
                ))}
              </Grid>

              <TextField fullWidth multiline minRows={2} label="Notas internas" value={editing.notes || ""} onChange={(event) => setEditing((current) => ({ ...current, notes: event.target.value }))} />
            </Stack>
          </DialogContent>
        ) : null}
        <Divider />
        <DialogActions sx={{ p: { xs: 1.25, sm: 2 }, position: { xs: "sticky", sm: "static" }, bottom: 0, zIndex: 2, bgcolor: "#fff", borderTop: { xs: `1px solid ${BRAND.border}`, sm: 0 } }}>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={saveMember} disabled={saving}>{saving ? "Guardando..." : "Guardar acceso"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
